import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Filter, MapPin, FolderOpen } from "lucide-react";
import { api } from "@/lib/api";
import { usePageSEO } from "@/lib/seo";
import { PROGRAM_BY_KEY, PROJECT_STATUS_LABEL, PROJECT_STATUS_COLOR } from "@/lib/programs";

const THEMES = [
  { key: "all", label: "Alle projecten" },
  { key: "herstel", label: "Herstel" },
  { key: "bestaanszekerheid", label: "Bestaanszekerheid" },
  { key: "groei-kansen", label: "Groei & Kansen" },
  { key: "community", label: "Community" },
];

const STATUSES = [
  { key: "all", label: "Alle statussen" },
  { key: "actief", label: "Actief" },
  { key: "in-ontwikkeling", label: "In ontwikkeling" },
  { key: "idee", label: "Idee" },
  { key: "afgerond", label: "Afgerond" },
];

export default function ProjectenPage() {
  usePageSEO({
    title: "Projecten | Stichting FIN",
    description:
      "Alle projecten van Stichting FIN — Herstel, Bestaanszekerheid, Groei & Kansen en Community. Filter op programma, status of locatie.",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [theme, setTheme] = useState(searchParams.get("thema") || "all");
  const [status, setStatus] = useState("all");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTheme(searchParams.get("thema") || "all");
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get("/projects")
      .then((r) => !cancelled && setProjects(r.data || []))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () =>
      projects.filter((p) => {
        const themeOk = theme === "all" || p.theme === theme;
        const statusOk = status === "all" || p.status === status;
        return themeOk && statusOk;
      }),
    [projects, theme, status]
  );

  const setThemeAndUrl = (t) => {
    setTheme(t);
    const next = new URLSearchParams(searchParams);
    if (t === "all") next.delete("thema");
    else next.set("thema", t);
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="projecten-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Projecten</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight text-balance">
            Concrete projecten. <em className="italic text-gold">Concrete impact.</em>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            Van pilots die net beginnen tot lopende programma's. Filter op programma en status.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-5 border-b border-line bg-cream sticky top-[76px] md:top-[84px] z-30 backdrop-blur">
        <div className="container-x flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-gold shrink-0" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-petrol mr-1">Programma</span>
            {THEMES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setThemeAndUrl(t.key)}
                className={`text-xs md:text-[13px] rounded-full px-3 py-1.5 border transition ${
                  theme === t.key
                    ? "bg-petrol text-cream border-petrol"
                    : "bg-ivory text-inkMuted border-line hover:border-gold hover:text-petrol"
                }`}
                data-testid={`filter-thema-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-petrol mr-1">Status</span>
            {STATUSES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatus(s.key)}
                className={`text-xs md:text-[13px] rounded-full px-3 py-1.5 border transition ${
                  status === s.key
                    ? "bg-gold text-petrol border-gold"
                    : "bg-ivory text-inkMuted border-line hover:border-gold hover:text-petrol"
                }`}
                data-testid={`filter-status-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lijst */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="container-x">
          {loading && (
            <div className="text-inkMuted text-center py-16" data-testid="projecten-loading">
              Projecten laden…
            </div>
          )}
          {error && (
            <div className="text-red-600 text-center py-16" data-testid="projecten-error">
              Kon projecten niet laden ({error}).
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="bg-sandSoft border border-line rounded-3xl p-10 md:p-14 text-center" data-testid="projecten-empty">
              <FolderOpen size={32} className="mx-auto text-gold mb-4" />
              <p className="font-display text-2xl text-petrol mb-2">Nog geen projecten in deze filter</p>
              <p className="text-inkMuted text-[15px] max-w-lg mx-auto">
                We werken aan de opbouw van dit programma. Neem gerust contact op als u wilt weten wat er in ontwikkeling is.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-6 bg-petrol text-cream rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-petrolDark transition"
              >
                Neem contact op
              </Link>
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projecten-list">
              {filtered.map((p) => {
                const prog = PROGRAM_BY_KEY[p.theme];
                return (
                  <article
                    key={p.id}
                    className="bg-ivory border border-line rounded-3xl overflow-hidden flex flex-col hover:shadow-card transition"
                    data-testid={`project-card-${p.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-sand relative">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-sand to-goldSoft">
                          <span className="text-petrol/70 font-display italic text-4xl">FIN</span>
                        </div>
                      )}
                      {prog && (
                        <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-cream/95 backdrop-blur rounded-full px-3 py-1.5">
                          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-petrol">{prog.label}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${PROJECT_STATUS_COLOR[p.status] || "text-ink/60"}`}>
                          {PROJECT_STATUS_LABEL[p.status] || p.status}
                        </span>
                        {p.locatie && (
                          <>
                            <span className="text-ink/30">·</span>
                            <span className="inline-flex items-center gap-1 text-[11.5px] text-inkMuted">
                              <MapPin size={11} /> {p.locatie}
                            </span>
                          </>
                        )}
                      </div>
                      <h3 className="font-display text-2xl text-petrol leading-tight mb-2">{p.title}</h3>
                      <p className="text-inkMuted text-[15px] leading-relaxed flex-1 text-pretty">{p.description_short}</p>
                      <Link
                        to={`/projecten/${p.slug}`}
                        className="mt-5 inline-flex items-center gap-1.5 text-gold font-semibold text-sm hover:text-goldDark transition"
                        data-testid={`project-link-${p.slug}`}
                      >
                        Bekijk project <ArrowRight size={14} />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
